import { construirUrlWhatsApp } from './logicaMensajes';
import { serializarHorarioSemanal } from './horarioCatalogo';
import { listaAgencias, MAX_AGENCIAS } from './logicaEntrega';
import { esCobroAutomatico } from './pasarelas';
import { simboloMoneda } from './moneda';

export const MAX_PRODUCTOS_CATALOGO = 100;
// Paquetes públicos: catalogosPublicos/{gymId}/paquetes/{indice} con hasta 50
// productos cada uno. Abrir el catálogo lee 1-2 documentos en lugar de N.
export const PRODUCTOS_POR_PAQUETE = 50;
export const MAX_PAQUETES_CATALOGO = Math.ceil(MAX_PRODUCTOS_CATALOGO / PRODUCTOS_POR_PAQUETE);
export const validarIdCatalogo = valor => {
  if (typeof valor !== 'string' || !/^[a-zA-Z0-9_-]{1,120}$/.test(valor)) throw new Error('Identificador de catálogo inválido');
  return valor;
};
const texto = (valor, maximo, obligatorio = false) => {
  const limpio = String(valor ?? '').trim();
  if ((obligatorio && !limpio) || limpio.length > maximo) throw new Error(`Completa los textos del catálogo (máximo ${maximo} caracteres)`);
  return limpio;
};
const METODOS_PAGO_CATALOGO = ['yape', 'plin', 'transferencia', 'tarjeta'];
const colorHex = (valor, respaldo) => /^#[0-9a-fA-F]{6}$/.test(String(valor || '')) ? String(valor) : respaldo;
const logoCatalogo = valor => {
  const logo = String(valor || '');
  if (!logo) return '';
  if (logo.length > 700000 || !/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(logo)) {
    throw new Error('El logo público debe ser PNG, JPG o WebP y pesar menos de 500 KB');
  }
  return logo;
};
export const normalizarWhatsAppCatalogo = valor => {
  const numero = String(valor ?? '').replace(/[\s()+-]/g, '');
  const internacional = /^9\d{8}$/.test(numero) ? `51${numero}` : numero;
  if (!/^519\d{8}$/.test(internacional)) throw new Error('Ingresa un celular peruano de 9 dígitos, con o sin +51');
  return internacional;
};
export const validarFotoCatalogo = valor => {
  const foto = texto(valor, 1500);
  if (!foto) return '';
  let url;
  try { url = new URL(foto); } catch { throw new Error('La foto debe ser una URL HTTPS válida'); }
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('La foto debe ser una URL HTTPS sin credenciales');
  if (url.href.length > 1500) throw new Error('La URL de la foto es demasiado larga');
  return url.href;
};

/** Ropa: una foto por color ({ color: URL HTTPS }), máximo 20, en orden estable (igual que el backend). */
export const validarFotosColor = valor => {
  if (valor == null) return {};
  if (typeof valor !== 'object' || Array.isArray(valor)) throw new Error('Las fotos por color no son válidas');
  const entradas = Object.entries(valor).map(([color, url]) => [texto(color, 30, true), validarFotoCatalogo(url)]).filter(([, url]) => url);
  if (entradas.length > 20) throw new Error('Configura como máximo 20 fotos por color');
  return Object.fromEntries(entradas.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
};

/** Hasta 2 fotos adicionales del producto (3 con la principal), sin repetir (igual que el backend). */
export const MAX_FOTOS_EXTRA = 2;
export const validarFotosExtra = valor => {
  if (valor == null) return [];
  if (!Array.isArray(valor)) throw new Error('Las fotos adicionales no son válidas');
  const urls = [...new Set(valor.map(url => validarFotoCatalogo(url)).filter(Boolean))];
  if (urls.length > MAX_FOTOS_EXTRA) throw new Error(`Sube como máximo ${MAX_FOTOS_EXTRA + 1} fotos por producto`);
  return urls;
};

const agenciasCatalogo = valor => {
  const agencias = listaAgencias(valor);
  if (agencias.length > MAX_AGENCIAS) throw new Error(`Configura como máximo ${MAX_AGENCIAS} agencias de envío`);
  return agencias;
};

// Lista blanca explícita: no propagar objetos privados con ...producto.
export const normalizarPublicacionCatalogo = datos => {
  const productos = datos.productos || [];
  if (!Array.isArray(productos) || !productos.length || productos.length > MAX_PRODUCTOS_CATALOGO) {
    throw new Error(`Selecciona entre 1 y ${MAX_PRODUCTOS_CATALOGO} productos`);
  }
  const seleccion = productos.map(p => {
    const fotosColor = validarFotosColor(p.fotosColor);
    const fotosExtra = validarFotosExtra(p.fotosExtra);
    return {
      id: validarIdCatalogo(p.id), descripcion: texto(p.descripcion, 500), fotoUrl: validarFotoCatalogo(p.fotoUrl),
      ...(fotosExtra.length ? { fotosExtra } : {}),
      ...(Object.keys(fotosColor).length ? { fotosColor } : {}),
      categoria: texto(p.categoria || 'General', 80, true),
      ...(texto(p.marca, 60) ? { marca: texto(p.marca, 60) } : {}),
    };
  }).sort((a, b) => a.id.localeCompare(b.id));
  if (new Set(seleccion.map(p => p.id)).size !== seleccion.length) throw new Error('Hay productos repetidos');
  const metodosPago = [...new Set((datos.metodosPago === undefined ? ['yape'] : datos.metodosPago || []).filter(m => METODOS_PAGO_CATALOGO.includes(m)))];
  if (!metodosPago.length) throw new Error('Selecciona al menos un método de pago aceptado');
  return {
    gymId: validarIdCatalogo(datos.gymId), nombre: texto(datos.nombre, 100, true),
    descripcion: texto(datos.descripcion, 500), direccion: texto(datos.direccion, 200),
    whatsapp: normalizarWhatsAppCatalogo(datos.whatsapp), horario: texto(datos.horario, 200),
    horarioSemanal: serializarHorarioSemanal(datos.horarioSemanal),
    metodosPago, deliveryHabilitado: datos.deliveryHabilitado === true,
    notaDelivery: texto(datos.notaDelivery, 200),
    ...(datos.envioProvinciaHabilitado === true ? { envioProvinciaHabilitado: true,
      agenciasEnvio: agenciasCatalogo(datos.agenciasEnvio), notaEnvio: texto(datos.notaEnvio, 200) } : {}),
    marca: {
      logoBase64: logoCatalogo(datos.marca?.logoBase64),
      colorPrimario: colorHex(datos.marca?.colorPrimario, '#DC2626'),
      colorSecundario: colorHex(datos.marca?.colorSecundario, '#991B1B'),
    },
    productos: seleccion,
  };
};

// Rearma la lista plana de productos ({ id, gymId, version, ...proyección }, la
// misma forma que la subcolección legada) desde los paquetes vigentes de la cabecera.
export const productosDePaquetes = (cabecera, paquetes = []) => paquetes
  .filter(paquete => Number.isInteger(paquete?.indice) && paquete.indice < Number(cabecera?.paquetes))
  .flatMap(paquete => Object.entries(paquete.productos || {})
    .map(([id, datos]) => ({ id, gymId: paquete.gymId, version: paquete.version, ...datos })));

// Solo se pinta un catálogo completo y de una sola publicación: nunca mezcla versiones.
export const catalogoCoherente = (cabecera, productos) => Array.isArray(cabecera?.productoIds)
  && productos.length === cabecera.productoIds.length
  && productos.every(p => p.version === cabecera.version && cabecera.productoIds.includes(p.id));

export const ordenarProductosCatalogo = productos => [...productos]
  .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));

// 'manual' | 'automatico_pruebas' | 'automatico_produccion' copiado del gimnasio al publicar; null en
// catálogos publicados antes de este campo (hasta republicarlos). Es una guía
// para los textos: el modo autoritativo lo devuelve ordenOperacion al crear.
export const modoCobroCatalogo = cabecera => cabecera?.modoCobro === 'manual' || esCobroAutomatico(cabecera?.modoCobro) ? cabecera.modoCobro : null;

export const enlaceConsultaCatalogo = ({ catalogo, producto, cantidad = 1, enlace }) => {
  const unidades = Number(cantidad);
  if (!Number.isInteger(unidades) || unidades < 1 || unidades > 99) return null;
  const numero = normalizarWhatsAppCatalogo(catalogo.whatsapp);
  const mensaje = `Hola ${catalogo.nombre}, quisiera consultar por:\n`
    + `${unidades} × ${producto.nombre}\nPrecio publicado por ${producto.unidad || 'unidad'}: ${simboloMoneda()} ${Number(producto.precio).toFixed(2)}\n`
    + `Referencia: ${producto.id}\n${enlace}\n\n¿Me confirman precio, disponibilidad y cómo coordinar la entrega o retiro?`;
  return construirUrlWhatsApp(numero, mensaje, '');
};
